import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { Formik } from 'formik';
import SubmitButton from './SubmitButton';
import Button from './Button';
import Label from './Label';
import AddressLookup, { Address } from './AddressLookup';
import DayPickerInputValue from './edit/DayPickerInputValue';
import { CalendarIcon, TruckIcon, EyeIcon, XMarkIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { useDateDisabler } from '../utilities/DateDisabler';
import { axiosKK } from '../services/networkRequest';
import {
  OrderBoxesResponse,
  BackendShippingAddress,
  CreateDPDLabelRequest,
  CreateDPDLabelResponse,
  DPDLabel,
  DPDService,
  ListDPDLabelsResponse,
  ListDPDServicesResponse,
  DPDTrackingData,
  DPDTrackingResponse,
  DPDPodImageResponse,
  CreateDPDCollectionResponse,
  CancelDPDCollectionResponse,
  DPDCollectionDate,
  DPDCollectionDatesResponse,
} from '../models/APIModels';
import Loading from './Loading';
import moment from 'moment';

interface DPDShippingModalProps {
  isOpen: boolean;
  close: () => void;
  collectionAddress: string;
  deliveryAddress: string;
  orderBoxes?: OrderBoxesResponse[];
  orderId: number;
  customer?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  };
  orderCollectionDate?: number;
}

interface DPDFormValues {
  numberOfParcels: number;
  totalWeight: number;
  collectionDate?: Date;
  deliveryInstructions: string;
  shippingRef1: string;
  networkCode: string;
  collectionAddress: BackendShippingAddress;
  deliveryAddress: BackendShippingAddress;
  customCollectionAddress: Address;
  customDeliveryAddress: Address;
}

interface DPDFormErrors {
  numberOfParcels?: string;
  totalWeight?: string;
  collectionDate?: string;
  deliveryInstructions?: string;
  shippingRef1?: string;
  collectionAddress?: string;
  deliveryAddress?: string;
}

const DEFAULT_NETWORK_CODE = '2^12';
const DPD_MAX_WEIGHT_PER_PARCEL = 30;

const KIT_KEEPER_WAREHOUSE_ADDRESS: BackendShippingAddress = {
  contactName: 'Steve Priest',
  organisationName: 'KIT KEEPER LTD',
  addressLine1: 'UNIT 7',
  addressLine2: 'WHITE HORSE BUSINESS PARK, WARE',
  town: 'FARINGDON',
  county: 'OXFORDSHIRE',
  postcode: 'SN7 8NY',
  countryCode: 'GB',
  phone: '01367718710',
  email: 'operations@kitkeeper.co.uk',
};

const parseAddressString = (
  addressString: string,
): Partial<BackendShippingAddress> => {
  const parts = addressString.split(', ').filter((p) => p.trim());
  return {
    addressLine1: parts[0] || '',
    addressLine2: parts.length > 3 ? parts[1] : '',
    town: parts[parts.length - 3] || '',
    postcode: parts[parts.length - 2] || '',
    countryCode: 'GB',
  };
};

const addressToLookup = (addr: BackendShippingAddress): Address => ({
  line1: addr.addressLine1,
  line2: addr.addressLine2,
  city: addr.town,
  county: addr.county,
  postCode: addr.postcode,
  country: 'United Kingdom',
});

const lookupToAddress = (
  lookup: Address,
  base: BackendShippingAddress,
): BackendShippingAddress => ({
  ...base,
  addressLine1: lookup.line1 || '',
  addressLine2: lookup.line2 || '',
  town: lookup.city || '',
  county: lookup.county || '',
  postcode: lookup.postCode || '',
  countryCode: 'GB',
});

const printDpdLabel = (labelHtml: string) => {
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(labelHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }
};

const customStyles = {
  content: {
    display: 'flex',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
};

export default function DPDShippingModal({
  isOpen,
  close,
  collectionAddress,
  deliveryAddress,
  orderBoxes,
  orderId,
  customer,
  orderCollectionDate,
}: DPDShippingModalProps): JSX.Element {
  const [existingLabels, setExistingLabels] = useState<DPDLabel[]>([]);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [createdLabel, setCreatedLabel] = useState<DPDLabel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [availableServices, setAvailableServices] = useState<DPDService[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Tracking state
  const [trackingLabelId, setTrackingLabelId] = useState<number | null>(null);
  const [trackingData, setTrackingData] = useState<DPDTrackingData | null>(null);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [podImage, setPodImage] = useState<string | null>(null);
  const [loadingPod, setLoadingPod] = useState(false);
  const [showPodModal, setShowPodModal] = useState(false);

  // Collection state
  const collectionSectionRef = React.useRef<HTMLDivElement>(null);
  const [showCollectionForm, setShowCollectionForm] = useState(false);
  const [collectionDate, setCollectionDate] = useState<Date | undefined>(undefined);
  const [availableCollectionDates, setAvailableCollectionDates] = useState<DPDCollectionDate[]>([]);
  const [loadingCollectionDates, setLoadingCollectionDates] = useState(false);
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [cancellingCollectionId, setCancellingCollectionId] = useState<number | null>(null);

  const baseDisabledDays = useDateDisabler(null, null, 1);

  // Cap the label collection date to 5 working days ahead
  const disabledDays = React.useMemo(() => {
    const today = new Date();
    let workingDaysCount = 0;
    let maxDate = new Date(today);
    while (workingDaysCount < 5) {
      maxDate.setDate(maxDate.getDate() + 1);
      const dayOfWeek = maxDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDaysCount++;
      }
    }
    const rules = Array.isArray(baseDisabledDays) ? [...baseDisabledDays] : [baseDisabledDays];
    rules.push({ after: maxDate });
    return rules;
  }, [baseDisabledDays]);

  const parsedDelivery = parseAddressString(deliveryAddress);
  const initialDeliveryAddress: BackendShippingAddress = {
    contactName:
      `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim() || '',
    organisationName: '',
    addressLine1: parsedDelivery.addressLine1 || '',
    addressLine2: parsedDelivery.addressLine2 || '',
    town: parsedDelivery.town || '',
    county: '',
    postcode: parsedDelivery.postcode || '',
    countryCode: 'GB',
    phone: customer?.phoneNumber || '',
    email: customer?.email || '',
  };

  const shippableBoxCount = orderBoxes?.filter((ob) => ob.box?.chargeShipping !== false).length || 1;

  const initialValues: DPDFormValues = {
    numberOfParcels: shippableBoxCount,
    totalWeight: shippableBoxCount * DPD_MAX_WEIGHT_PER_PARCEL,
    collectionDate: orderCollectionDate ? new Date(orderCollectionDate) : undefined,
    deliveryInstructions: '',
    shippingRef1: `ORDER-${orderId}`.substring(0, 25),
    networkCode: DEFAULT_NETWORK_CODE,
    collectionAddress: { ...KIT_KEEPER_WAREHOUSE_ADDRESS },
    deliveryAddress: initialDeliveryAddress,
    customCollectionAddress: addressToLookup(KIT_KEEPER_WAREHOUSE_ADDRESS),
    customDeliveryAddress: addressToLookup(initialDeliveryAddress),
  };

  useEffect(() => {
    if (isOpen) {
      setLoadingLabels(true);
      setError(null);
      setCreatedLabel(null);

      axiosKK
        .get<ListDPDLabelsResponse>(
          `/admin/order/${orderId}/shipping/labels`,
        )
        .then(({ data }) => {
          setExistingLabels(data.labels || []);
        })
        .catch((err) => {
          console.error('Failed to fetch existing labels:', err);
        })
        .finally(() => {
          setLoadingLabels(false);
        });
    }
  }, [isOpen, orderId]);

  const fetchServices = (collectionPostcode: string, deliveryPostcode: string) => {
    if (!collectionPostcode || !deliveryPostcode) return;
    setLoadingServices(true);
    axiosKK
      .get<ListDPDServicesResponse>('/admin/shipping/services', {
        params: { collectionPostcode, deliveryPostcode },
      })
      .then(({ data }) => {
        setAvailableServices(data.services || []);
      })
      .catch((err) => {
        console.error('Failed to fetch DPD services:', err);
        setAvailableServices([]);
      })
      .finally(() => {
        setLoadingServices(false);
      });
  };

  useEffect(() => {
    if (isOpen) {
      fetchServices(
        KIT_KEEPER_WAREHOUSE_ADDRESS.postcode,
        initialDeliveryAddress.postcode,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handlePrintExistingLabel = (labelId: number) => {
    const label = existingLabels.find((l) => l.id === labelId);
    if (label?.labelData) {
      printDpdLabel(label.labelData);
    } else {
      setError('Label data not available for printing');
    }
  };

  const handleViewTracking = (labelId: number) => {
    if (trackingLabelId === labelId) {
      setTrackingLabelId(null);
      setTrackingData(null);
      setPodImage(null);
      return;
    }
    setTrackingLabelId(labelId);
    setLoadingTracking(true);
    setTrackingData(null);
    setPodImage(null);

    axiosKK
      .get<DPDTrackingResponse>(`/admin/shipping/label/${labelId}/tracking`)
      .then(({ data }) => {
        setTrackingData(data.tracking);
      })
      .catch((err) => {
        console.error('Failed to fetch tracking:', err);
        setError('Failed to fetch tracking information');
      })
      .finally(() => {
        setLoadingTracking(false);
      });
  };

  const handleViewPod = (labelId: number) => {
    setLoadingPod(true);
    axiosKK
      .get<DPDPodImageResponse>(`/admin/shipping/label/${labelId}/pod-image`)
      .then(({ data }) => {
        setPodImage(data.imageBase64);
        setShowPodModal(true);
      })
      .catch((err) => {
        console.error('Failed to fetch POD image:', err);
        setError('Failed to fetch proof of delivery image');
      })
      .finally(() => {
        setLoadingPod(false);
      });
  };

  const fetchCollectionDates = () => {
    const postcode = KIT_KEEPER_WAREHOUSE_ADDRESS.postcode;
    setLoadingCollectionDates(true);
    axiosKK
      .get<DPDCollectionDatesResponse>('/admin/shipping/collection-dates', {
        params: { postcode },
      })
      .then(({ data }) => {
        setAvailableCollectionDates(data.dates || []);
      })
      .catch((err) => {
        console.error('Failed to fetch collection dates:', err);
        setAvailableCollectionDates([]);
        setError('Failed to fetch available collection dates');
      })
      .finally(() => {
        setLoadingCollectionDates(false);
      });
  };

  const handleCreateCollection = () => {
    if (!collectionDate) {
      setError('Please select a collection date');
      return;
    }
    setCreatingCollection(true);
    setError(null);
    const formattedDate = moment(collectionDate).format('YYYY-MM-DD');

    // Find the selected date's time windows from the API response
    const selectedDateInfo = availableCollectionDates.find((d) => d.date === formattedDate);

    // Calculate parcel count from non-voided delivery labels
    const activeDeliveryLabels = existingLabels.filter(
      (l) => l.status !== 'voided' && (l.shipmentType || 'delivery') !== 'collection'
    );
    const parcelCount = activeDeliveryLabels.length || 1;

    axiosKK
      .post<CreateDPDCollectionResponse>('/admin/shipping/collection', {
        orderId,
        collectionDate: formattedDate,
        collectionAddress: KIT_KEEPER_WAREHOUSE_ADDRESS,
        parcelQuantity: parcelCount,
        totalWeight: parcelCount * DPD_MAX_WEIGHT_PER_PARCEL,
        readyTime: selectedDateInfo?.firstInArea || '09:00',
        closeTime: selectedDateInfo?.lastInArea || '17:00',
        shippingRef1: `ORDER-${orderId}`.substring(0, 25),
      })
      .then(({ data }) => {
        const newLabel = data?.collection;
        if (newLabel?.id) {
          setExistingLabels((prev) => [newLabel, ...prev]);
        }
        setShowCollectionForm(false);
        setCollectionDate(undefined);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to create collection:', err);
        const errorMsg = err.response?.data?.error;
        setError(
          typeof errorMsg === 'string'
            ? errorMsg
            : typeof errorMsg === 'object' && errorMsg !== null
              ? JSON.stringify(errorMsg)
              : 'Failed to create DPD collection',
        );
      })
      .finally(() => {
        setCreatingCollection(false);
      });
  };

  const handleCancelCollection = (labelId: number) => {
    if (!window.confirm('Are you sure you want to cancel this collection?')) return;
    setCancellingCollectionId(labelId);
    axiosKK
      .post<CancelDPDCollectionResponse>(
        `/admin/shipping/collection/${labelId}/cancel`,
      )
      .then(({ data }) => {
        const updatedLabel = data?.collection;
        setExistingLabels((prev) =>
          prev.map((l) =>
            l.id === labelId
              ? updatedLabel?.id
                ? { ...updatedLabel, status: 'cancelled' }
                : { ...l, status: 'cancelled' }
              : l,
          ),
        );
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || '';
        if (msg.toLowerCase().includes('already cancelled')) {
          setError('This collection has already been cancelled');
        } else {
          console.error('Failed to cancel collection:', err);
          setError('Failed to cancel collection');
        }
      })
      .finally(() => {
        setCancellingCollectionId(null);
      });
  };


  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      style={customStyles}
      contentLabel="DPD Shipping Modal"
      ariaHideApp={false}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validate={(values) => {
          const errors: DPDFormErrors = {};

          if (!values.numberOfParcels || values.numberOfParcels < 1) {
            errors.numberOfParcels = 'Must be at least 1';
          } else if (values.numberOfParcels > 99) {
            errors.numberOfParcels = 'Must be 99 or fewer';
          }

          if (!values.totalWeight || values.totalWeight <= 0) {
            errors.totalWeight = 'Weight must be greater than 0';
          } else if (values.totalWeight > 99999) {
            errors.totalWeight = 'Weight must be 99999 or less';
          }

          if (!values.collectionDate) {
            errors.collectionDate = 'Please select a collection date';
          }

          if (!values.collectionAddress.addressLine1) {
            errors.collectionAddress = 'Collection address line 1 is required';
          } else if (!values.collectionAddress.town) {
            errors.collectionAddress = 'Collection town is required';
          } else if (!values.collectionAddress.postcode) {
            errors.collectionAddress = 'Collection postcode is required';
          }

          if (!values.deliveryAddress.contactName) {
            errors.deliveryAddress = 'Delivery contact name is required';
          } else if (!values.deliveryAddress.addressLine1) {
            errors.deliveryAddress = 'Delivery address line 1 is required';
          } else if (!values.deliveryAddress.town) {
            errors.deliveryAddress = 'Delivery town is required';
          } else if (!values.deliveryAddress.postcode) {
            errors.deliveryAddress = 'Delivery postcode is required';
          } else if (!values.deliveryAddress.phone) {
            errors.deliveryAddress = 'Delivery phone is required';
          } else if (!values.deliveryAddress.email) {
            errors.deliveryAddress = 'Delivery email is required';
          }

          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          setError(null);

          const formattedDate = moment(values.collectionDate).format(
            'YYYY-MM-DD',
          );
          const collectionDateStr = `${formattedDate}T09:00:00`;

          const requestData: CreateDPDLabelRequest = {
            orderId,
            collectionAddress: values.collectionAddress,
            deliveryAddress: values.deliveryAddress,
            numberOfParcels: values.numberOfParcels,
            totalWeight: values.totalWeight,
            networkCode: values.networkCode,
            collectionDate: collectionDateStr,
            shippingRef1: values.shippingRef1,
            deliveryInstructions: values.deliveryInstructions,
          };

          axiosKK
            .post<CreateDPDLabelResponse>(
              '/admin/shipping/label',
              requestData,
            )
            .then(({ data }) => {
              setCreatedLabel(data.label);
              setExistingLabels((prev) => [data.label, ...prev]);
              setSubmitting(false);
              setTimeout(() => {
                collectionSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }, 300);
            })
            .catch((err) => {
              console.error('Failed to create DPD label:', err);
              setError(
                err.response?.data?.error ||
                  'Failed to create DPD shipping label',
              );
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          errors,
          touched,
          isSubmitting,
          isValid,
          setFieldValue,
          resetForm,
        }) => (
          <form onSubmit={handleSubmit} className="dpd-shipping-form">
            <div className="modal-header">
              <h3>DPD Shipping Label</h3>
              <SubmitButton red={true} type="button" onClick={close}>
                Close
              </SubmitButton>
            </div>

            <div className="modal-content">
              {loadingLabels && (
                <div className="loading-message">
                  <p>Loading existing labels...</p>
                  <Loading />
                </div>
              )}

              {error && (
                <div className="error-message dpd-error-dismissible">
                  <span className="error">{error}</span>
                  <Button
                    variant="tertiary"
                    size="sm"
                    iconOnly
                    className="dpd-error-dismiss"
                    aria-label="Dismiss error"
                    onClick={() => setError(null)}
                  >
                    <XMarkIcon aria-hidden="true" />
                  </Button>
                </div>
              )}

              {/* Existing Labels */}
              {existingLabels.length > 0 && (
                <div className="existing-labels">
                  <h4>Existing Labels</h4>
                  <div className="labels-list">
                    {existingLabels.filter((l) => l?.id).map((label) => (
                      <div key={label.id} className={`label-card ${trackingLabelId === label.id ? 'tracking-expanded' : ''}`}>
                        <div className="label-card-header">
                          <div className="label-card-info">
                            <span className={`dpd-type-badge dpd-type-${label.shipmentType || 'delivery'}`}>
                              {(label.shipmentType || 'delivery') === 'collection' ? 'Collection' : 'Delivery'}
                            </span>
                            <span className="label-card-date">
                              {moment(label.createdAt).format('DD/MM/YYYY')}
                            </span>
                          </div>
                          <div className="label-card-actions">
                            {(label.shipmentType || 'delivery') !== 'collection' ? (
                              <>
                                <button
                                  type="button"
                                  className="label-action-btn"
                                  onClick={() => handleViewTracking(label.id)}
                                  title="Tracking"
                                >
                                  {trackingLabelId === label.id ? (
                                    <ChevronUpIcon style={{ width: '16px', height: '16px' }} />
                                  ) : (
                                    <TruckIcon style={{ width: '16px', height: '16px' }} />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="label-action-btn"
                                  onClick={() => handlePrintExistingLabel(label.id)}
                                  title="Print"
                                >
                                  Print
                                </button>
                                {label.dpdPodImageKey && (
                                  <button
                                    type="button"
                                    className="label-action-btn"
                                    disabled={loadingPod}
                                    onClick={() => handleViewPod(label.id)}
                                    title="Proof of Delivery"
                                  >
                                    <EyeIcon style={{ width: '16px', height: '16px' }} />
                                  </button>
                                )}
                              </>
                            ) : (
                              <button
                                type="button"
                                className="label-action-btn label-action-btn-danger"
                                disabled={cancellingCollectionId === label.id}
                                onClick={() => handleCancelCollection(label.id)}
                              >
                                {cancellingCollectionId === label.id ? 'Cancelling...' : 'Cancel'}
                              </button>
                            )}
                          </div>
                        </div>
                        {(label.trackingNumber || label.parcelNumber) && (
                          <div className="label-card-details">
                            {label.trackingNumber && <span>Tracking: {label.trackingNumber}</span>}
                            {label.parcelNumber && <span>Parcel: {label.parcelNumber}</span>}
                          </div>
                        )}

                        {/* Tracking detail */}
                        {trackingLabelId === label.id && (
                          <div className="label-card-tracking">
                                {loadingTracking ? (
                                  <div className="tracking-loading">
                                    <Loading />
                                    <span>Loading tracking...</span>
                                  </div>
                                ) : trackingData ? (
                                  <div className="tracking-panel">
                                    <div className="tracking-summary">
                                      <div className="tracking-summary-item">
                                        <span className="tracking-summary-label">Status</span>
                                        <span className="tracking-summary-value">{trackingData.deliveryStatus}</span>
                                      </div>
                                      <div className="tracking-summary-item">
                                        <span className="tracking-summary-label">Current</span>
                                        <span className="tracking-summary-value">{trackingData.parcelStatus}</span>
                                      </div>
                                      {trackingData.deliveredToName && (
                                        <div className="tracking-summary-item">
                                          <span className="tracking-summary-label">Delivered To</span>
                                          <span className="tracking-summary-value">{trackingData.deliveredToName}</span>
                                        </div>
                                      )}
                                      {trackingData.deliveryDate && (
                                        <div className="tracking-summary-item">
                                          <span className="tracking-summary-label">Delivery Date</span>
                                          <span className="tracking-summary-value">
                                            {moment(trackingData.deliveryDate).format('DD/MM/YYYY')}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    {trackingData.events.length > 0 && (
                                      <div className="tracking-events">
                                        <h5>Tracking Events</h5>
                                        <div className="tracking-timeline">
                                          {trackingData.events.map((event, index) => (
                                            <div
                                              key={`${event.date}-${event.time}-${index}`}
                                              className={`tracking-event ${index === 0 ? 'tracking-event-latest' : ''}`}
                                            >
                                              <div className="tracking-event-marker" />
                                              <div className="tracking-event-content">
                                                <div className="tracking-event-time">
                                                  {moment(event.date, 'YYYY-MM-DD').format('DD/MM/YYYY')} {event.time.substring(0, 5)}
                                                </div>
                                                <div className="tracking-event-status">{event.eventStatus}</div>
                                                {event.locality && (
                                                  <div className="tracking-event-location">{event.locality}</div>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="tracking-empty">No tracking information available</div>
                                )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Form */}
              {!loadingLabels && (
                <>
                  <div className="form-group">
                    <Label htmlFor="numberOfParcels">Number of Parcels</Label>
                    <input
                      id="numberOfParcels"
                      name="numberOfParcels"
                      type="number"
                      min="1"
                      max="99"
                      value={values.numberOfParcels}
                      onChange={(e) => {
                        handleChange(e);
                        const parcels = parseInt(e.target.value, 10);
                        if (!isNaN(parcels) && parcels > 0) {
                          setFieldValue('totalWeight', parcels * DPD_MAX_WEIGHT_PER_PARCEL);
                        }
                      }}
                      className="form-input"
                    />
                    {touched.numberOfParcels && errors.numberOfParcels && (
                      <span className="error">{errors.numberOfParcels}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="totalWeight">Total Weight (kg)</Label>
                    <input
                      id="totalWeight"
                      name="totalWeight"
                      type="number"
                      min="0.1"
                      step="0.1"
                      max="99999"
                      value={values.totalWeight}
                      onChange={handleChange}
                      className="form-input"
                    />
                    {touched.totalWeight && errors.totalWeight && (
                      <span className="error">{errors.totalWeight}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <div className="title-and-input" style={{ width: '100%' }}>
                      <div className="title" style={{ width: '100%' }}>
                        <Label>Collection Date:</Label>
                        <CalendarIcon
                          style={{
                            width: '30px',
                            height: '30px',
                            color: 'var(--kkUltraViolet)',
                          }}
                        />
                      </div>
                      <DayPickerInputValue
                        id="dpdCollectionDate"
                        handleStateChange={(day) =>
                          setFieldValue('collectionDate', day)
                        }
                        disabled={false}
                        disabledDays={disabledDays}
                      />
                    </div>
                    {touched.collectionDate && errors.collectionDate && (
                      <span className="error">{errors.collectionDate}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="shippingRef1">
                      Shipping Reference (max 25 chars)
                    </Label>
                    <input
                      id="shippingRef1"
                      name="shippingRef1"
                      type="text"
                      maxLength={25}
                      value={values.shippingRef1}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="deliveryInstructions">
                      Delivery Instructions (max 50 chars)
                    </Label>
                    <input
                      id="deliveryInstructions"
                      name="deliveryInstructions"
                      type="text"
                      maxLength={50}
                      value={values.deliveryInstructions}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="networkCode">DPD Service</Label>
                    <select
                      id="networkCode"
                      name="networkCode"
                      value={values.networkCode}
                      onChange={handleChange}
                      className="form-input dpd-service-select"
                      disabled={loadingServices}
                    >
                      {availableServices.length > 0 ? (
                        availableServices.map((service) => (
                          <option key={service.networkCode} value={service.networkCode}>
                            {service.networkDescription} ({service.networkCode})
                          </option>
                        ))
                      ) : (
                        <option value={DEFAULT_NETWORK_CODE}>
                          DPD Local Parcel Next Day (2^12){loadingServices ? ' — loading services...' : ''}
                        </option>
                      )}
                    </select>
                  </div>

                  {/* Collection Address */}
                  <div className="form-group address-section">
                    <h4>Collection Address (From)</h4>
                    <div className="address-fields">
                      <div className="address-field">
                        <Label htmlFor="collectionContactName">
                          Contact Name
                        </Label>
                        <input
                          id="collectionContactName"
                          type="text"
                          value={values.collectionAddress.contactName}
                          onChange={(e) =>
                            setFieldValue(
                              'collectionAddress.contactName',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="collectionOrg">Organisation</Label>
                        <input
                          id="collectionOrg"
                          type="text"
                          value={values.collectionAddress.organisationName}
                          onChange={(e) =>
                            setFieldValue(
                              'collectionAddress.organisationName',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="collectionPhone">Phone</Label>
                        <input
                          id="collectionPhone"
                          type="text"
                          value={values.collectionAddress.phone}
                          onChange={(e) =>
                            setFieldValue(
                              'collectionAddress.phone',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="collectionEmail">Email</Label>
                        <input
                          id="collectionEmail"
                          type="text"
                          value={values.collectionAddress.email}
                          onChange={(e) =>
                            setFieldValue(
                              'collectionAddress.email',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="custom-address-input">
                      <AddressLookup
                        address={values.customCollectionAddress}
                        setAddress={(address: Address) => {
                          setFieldValue('customCollectionAddress', address);
                          setFieldValue(
                            'collectionAddress',
                            lookupToAddress(
                              address,
                              values.collectionAddress,
                            ),
                          );
                        }}
                        onlyUnitedKingdom
                        idPrefix="dpd-from"
                      />
                    </div>
                    {touched.collectionAddress && errors.collectionAddress && (
                      <span className="error">{errors.collectionAddress}</span>
                    )}
                  </div>

                  {/* Delivery Address */}
                  <div className="form-group address-section">
                    <h4>Delivery Address (To)</h4>
                    <div className="address-fields">
                      <div className="address-field">
                        <Label htmlFor="deliveryContactName">
                          Contact Name *
                        </Label>
                        <input
                          id="deliveryContactName"
                          type="text"
                          value={values.deliveryAddress.contactName}
                          onChange={(e) =>
                            setFieldValue(
                              'deliveryAddress.contactName',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="deliveryPhone">Phone *</Label>
                        <input
                          id="deliveryPhone"
                          type="text"
                          value={values.deliveryAddress.phone}
                          onChange={(e) =>
                            setFieldValue(
                              'deliveryAddress.phone',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                      <div className="address-field">
                        <Label htmlFor="deliveryEmail">Email *</Label>
                        <input
                          id="deliveryEmail"
                          type="text"
                          value={values.deliveryAddress.email}
                          onChange={(e) =>
                            setFieldValue(
                              'deliveryAddress.email',
                              e.target.value,
                            )
                          }
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="custom-address-input">
                      <AddressLookup
                        address={values.customDeliveryAddress}
                        setAddress={(address: Address) => {
                          setFieldValue('customDeliveryAddress', address);
                          setFieldValue(
                            'deliveryAddress',
                            lookupToAddress(
                              address,
                              values.deliveryAddress,
                            ),
                          );
                        }}
                        onlyUnitedKingdom
                        idPrefix="dpd-to"
                      />
                    </div>
                    {touched.deliveryAddress && errors.deliveryAddress && (
                      <span className="error">{errors.deliveryAddress}</span>
                    )}
                  </div>
                </>
              )}

              {/* Success Banner */}
              {createdLabel && (
                <div className="dpd-success-banner">
                  <div className="dpd-success-banner-content">
                    <span className="dpd-success-banner-icon">&#10003;</span>
                    <div className="dpd-success-banner-text">
                      <strong>Label created successfully!</strong>
                      <span>
                        Tracking: {createdLabel.trackingNumber} | Parcel: {createdLabel.parcelNumber}
                      </span>
                    </div>
                  </div>
                  <div className="dpd-success-banner-actions">
                    <SubmitButton
                      type="button"
                      onClick={() => {
                        if (createdLabel.labelData) {
                          printDpdLabel(createdLabel.labelData);
                        } else {
                          handlePrintExistingLabel(createdLabel.id);
                        }
                      }}
                    >
                      Print Label
                    </SubmitButton>
                    <SubmitButton
                      type="button"
                      onClick={() => {
                        setCreatedLabel(null);
                        resetForm();
                      }}
                    >
                      Create Another
                    </SubmitButton>
                    <Button
                      variant="tertiary"
                      size="sm"
                      iconOnly
                      className="dpd-success-banner-dismiss"
                      aria-label="Dismiss"
                      onClick={() => setCreatedLabel(null)}
                    >
                      <XMarkIcon aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Collection Management */}
              {!loadingLabels && (() => {
                const hasActiveDeliveryLabels = existingLabels.some(
                  (l) => l.status !== 'voided' && (l.shipmentType || 'delivery') !== 'collection'
                );
                return (
                  <div className="dpd-collection-section" ref={collectionSectionRef}>
                    <div className="dpd-collection-header">
                      <h4>DPD Collections</h4>
                      <SubmitButton
                        type="button"
                        disabled={!hasActiveDeliveryLabels}
                        onClick={() => {
                          setShowCollectionForm(!showCollectionForm);
                          if (!showCollectionForm) {
                            fetchCollectionDates();
                          }
                        }}
                      >
                        {showCollectionForm ? 'Cancel' : 'Schedule Collection'}
                      </SubmitButton>
                    </div>
                    {!hasActiveDeliveryLabels && (
                      <p className="dpd-collection-disabled-hint">
                        Create a shipping label first to schedule a collection
                      </p>
                    )}
                    {showCollectionForm && (() => {
                      const selectedDateInfo = collectionDate
                        ? availableCollectionDates.find((d) => d.date === moment(collectionDate).format('YYYY-MM-DD'))
                        : undefined;
                      return (
                        <div className="dpd-collection-form">
                          <p className="dpd-collection-info">
                            DPD will collect from: {KIT_KEEPER_WAREHOUSE_ADDRESS.organisationName}, {KIT_KEEPER_WAREHOUSE_ADDRESS.addressLine1}, {KIT_KEEPER_WAREHOUSE_ADDRESS.town}, {KIT_KEEPER_WAREHOUSE_ADDRESS.postcode}
                          </p>
                          <div className="form-group">
                            <div className="title-and-input" style={{ width: '100%' }}>
                              <div className="title" style={{ width: '100%' }}>
                                <Label>Collection Date:</Label>
                                <CalendarIcon
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    color: 'var(--kkUltraViolet)',
                                  }}
                                />
                              </div>
                            </div>
                            {loadingCollectionDates ? (
                              <div className="loading-message">
                                <Loading />
                                <span>Loading available dates...</span>
                              </div>
                            ) : availableCollectionDates.length === 0 ? (
                              <div className="error-message">
                                <span className="error">No collection dates available. Please try again later.</span>
                              </div>
                            ) : (
                              <DayPickerInputValue
                                id="dpdCollectionPickupDate"
                                handleStateChange={(day) => setCollectionDate(day)}
                                disabled={false}
                                disabledDays={(day: Date) => {
                                  const formatted = moment(day).format('YYYY-MM-DD');
                                  return !availableCollectionDates.some((d) => d.date === formatted);
                                }}
                              />
                            )}
                          </div>
                          {selectedDateInfo && (
                            <div className="dpd-collection-time-info">
                              <span>Collection window: {selectedDateInfo.firstInArea} — {selectedDateInfo.lastInArea}</span>
                              <span>Cut-off: {selectedDateInfo.collectionCutOff}</span>
                            </div>
                          )}
                          <SubmitButton
                            type="button"
                            disabled={!collectionDate}
                            isSubmitting={creatingCollection}
                            onClick={handleCreateCollection}
                          >
                            Create Collection
                          </SubmitButton>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </div>

            <div className="modal-footer">
              <SubmitButton disabled={!isValid} isSubmitting={isSubmitting}>
                Create DPD Label
              </SubmitButton>
            </div>
          </form>
        )}
      </Formik>

      {/* POD Image Overlay */}
      {showPodModal && podImage && (
        <div className="dpd-pod-overlay" onClick={() => setShowPodModal(false)}>
          <div className="dpd-pod-container" onClick={(e) => e.stopPropagation()}>
            <div className="dpd-pod-header">
              <h4>Proof of Delivery</h4>
              <Button
                variant="tertiary"
                size="sm"
                iconOnly
                className="dpd-pod-close"
                aria-label="Close"
                onClick={() => setShowPodModal(false)}
              >
                <XMarkIcon aria-hidden="true" />
              </Button>
            </div>
            <div className="dpd-pod-image-wrapper">
              <img
                src={`data:image/jpeg;base64,${podImage}`}
                alt="Proof of Delivery"
                className="dpd-pod-image"
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
